// Small HTML tokenizer (WHATWG data state, simplified) shared by every email markup check:
// the structural allowlist, the save-time URL check and the post-render URL check.
// It reads real tags and attributes the way a browser does, so a quote or "href=" inside another
// attribute's value is never mistaken for an attribute. Only HTML whitespace separates tokens.
import {
  DEFAULT_IMAGE_PREFIXES,
  isAllowedEmailImageSrc,
  isValidImageDimension,
  normalizeImageStyle,
} from "./emailImages.ts";
import { decodeEntities } from "./htmlToText.ts";

const WS = "\t\n\f\r ";
const isWs = (c: string | undefined): boolean =>
  c !== undefined && c !== "" && WS.includes(c);
const isAlpha = (c: string | undefined): boolean => !!c && /^[A-Za-z]$/.test(c);
const VALID_NAME = /^[A-Za-z][A-Za-z0-9]*$/;

/** Trims HTML whitespace only (not U+00A0 or other Unicode spaces). */
export const htmlTrim = (s: string): string =>
  s.replace(/^[\t\n\f\r ]+|[\t\n\f\r ]+$/g, "");

const NBSP = String.fromCharCode(0xa0);

/**
 * Attribute value decoding as a browser does it: numeric references with or without ";", and &nbsp;
 * as U+00A0 (decodeEntities maps it to a plain space for the text part). References we do not know
 * stay raw, which only ever makes a URL fail the https:/mailto: check.
 */
export const decodeAttr = (raw: string): string =>
  decodeEntities(
    raw
      .replace(/&#([xX][0-9a-fA-F]+|[0-9]+);?/g, (_m, n: string) => `&#${n};`)
      .replace(/&nbsp;/g, NBSP),
  );

export type Attr = {
  name: string;
  raw: string;
  value: string;
  quoted: boolean;
  at: number;
};
export type TagToken = {
  type: "start" | "end";
  name: string;
  validName: boolean;
  attrs: Attr[];
  at: number;
  // GAVION batch 3: offset just past the tag (B3-T11 sanitiser)
  end: number;
};
// GAVION batch 3: `end` (offset past the token) when the token is complete (B3-T11 sanitiser)
export type Token = TagToken | { type: "other"; at: number; end?: number };
export type OpenAttr = { name: string; raw: string; quoted: boolean };
export type Tokenized = {
  tokens: Token[];
  /** Non-null when the input ends inside a tag or comment; inAttrValue is set inside (or right after "=" of) a value. */
  eof: { at: number; inAttrValue: OpenAttr | null } | null;
};

// Elements whose content is not parsed as markup.
const RAW_TEXT = new Set([
  "script",
  "style",
  "xmp",
  "iframe",
  "noembed",
  "noframes",
  "noscript",
  "textarea",
  "title",
]);

export function tokenize(s: string): Tokenized {
  const tokens: Token[] = [];
  const n = s.length;
  const eofAt = (
    at: number,
    inAttrValue: OpenAttr | null = null,
  ): Tokenized => ({
    tokens,
    eof: { at, inAttrValue },
  });
  let i = 0;
  while (i < n) {
    const lt = s.indexOf("<", i);
    if (lt === -1) break;
    const c = s[lt + 1];
    if (c === "!" || c === "?" || (c === "/" && !isAlpha(s[lt + 2]))) {
      if (c === "/" && lt + 2 >= n) break; // "</" at EOF is text
      let end: number;
      if (s.startsWith("<!--", lt)) {
        if (s.startsWith("<!-->", lt)) end = lt + 5;
        else if (s.startsWith("<!--->", lt)) end = lt + 6;
        else {
          const a = s.indexOf("-->", lt + 4);
          const b = s.indexOf("--!>", lt + 4);
          const e = [a === -1 ? -1 : a + 3, b === -1 ? -1 : b + 4].filter(
            (x) => x !== -1,
          );
          if (!e.length) {
            tokens.push({ type: "other", at: lt });
            return eofAt(lt);
          }
          end = Math.min(...e);
        }
      } else if (s.startsWith("</>", lt)) end = lt + 3;
      else {
        const gt = s.indexOf(">", lt + 2);
        if (gt === -1) {
          tokens.push({ type: "other", at: lt });
          return eofAt(lt);
        }
        end = gt + 1;
      }
      tokens.push({ type: "other", at: lt, end }); // GAVION batch 3: end
      i = end;
      continue;
    }
    const isEnd = c === "/";
    const nameStart = lt + (isEnd ? 2 : 1);
    if (!isAlpha(s[nameStart])) {
      i = lt + 1; // "<" not starting a tag is text
      continue;
    }
    let j = nameStart;
    while (j < n && !isWs(s[j]) && s[j] !== "/" && s[j] !== ">") j++;
    if (j >= n) return eofAt(lt);
    const rawName = s.slice(nameStart, j);
    const attrs: Attr[] = [];
    for (;;) {
      while (isWs(s[j])) j++;
      if (j >= n) return eofAt(lt);
      if (s[j] === ">") {
        j++;
        break;
      }
      if (s[j] === "/") {
        j++;
        continue;
      }
      const at = j;
      j++; // the first character (even "=") belongs to the name
      while (j < n && !isWs(s[j]) && !"/>=".includes(s[j])) j++;
      const name = s.slice(at, j).toLowerCase();
      while (isWs(s[j])) j++;
      if (s[j] !== "=") {
        attrs.push({ name, raw: "", value: "", quoted: false, at });
        continue;
      }
      j++;
      while (isWs(s[j])) j++;
      if (j >= n) return eofAt(lt, { name, raw: "", quoted: false });
      const q = s[j];
      let raw: string;
      let quoted = false;
      if (q === '"' || q === "'") {
        const close = s.indexOf(q, j + 1);
        if (close === -1)
          return eofAt(lt, { name, raw: s.slice(j + 1), quoted: true });
        raw = s.slice(j + 1, close);
        quoted = true;
        j = close + 1;
      } else if (q === ">") {
        raw = "";
      } else {
        const start = j;
        while (j < n && !isWs(s[j]) && s[j] !== ">") j++;
        if (j >= n)
          return eofAt(lt, { name, raw: s.slice(start), quoted: false });
        raw = s.slice(start, j);
      }
      attrs.push({ name, raw, value: decodeAttr(raw), quoted, at });
    }
    const name = rawName.toLowerCase();
    tokens.push({
      type: isEnd ? "end" : "start",
      name,
      validName: VALID_NAME.test(rawName),
      attrs,
      at: lt,
      end: j, // GAVION batch 3: offset past the tag
    });
    i = j;
    if (!isEnd && name === "plaintext") break;
    if (!isEnd && RAW_TEXT.has(name)) {
      const close = new RegExp(`</${name}(?=[\\t\\n\\f\\r />])`, "i").exec(
        s.slice(i),
      );
      if (!close) break;
      i += close.index;
    }
  }
  return { tokens, eof: null };
}

// ---------------------------------------------------------------------------------------------
// Structural allowlist: exactly what the TipTap editor (B5-T8) produces. Applies to every kind.
// GAVION batch 3: exported for the 1:1 send sanitiser (B3-T11)
export const ALLOWED_TAGS: ReadonlySet<string> = new Set([
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "a",
  "ul",
  "ol",
  "li",
  "span",
  "h1",
  "h2",
  "h3",
  "hr",
  "blockquote",
  // GAVION sig-image: our own hosted images only (emailImages.ts), with the attributes below.
  "img",
]);
const VOID_TAGS = new Set(["br", "hr", "img"]);
export const ALLOWED_ATTRS: ReadonlySet<string> = new Set([
  "class",
  "data-variable",
  "target",
  "rel",
]);

/** The only attributes an img may carry; src is checked against the image host allowlist. */
export const IMG_ATTRS: ReadonlySet<string> = new Set([
  "src",
  "alt",
  "width",
  "height",
  "style",
]);

/** True when one img attribute is allowed with this value (alt: any text without < or >). */
export function isAllowedImgAttr(
  name: string,
  value: string,
  imagePrefixes: readonly string[] = DEFAULT_IMAGE_PREFIXES,
): boolean {
  if (/[<>]/.test(value)) return false;
  switch (name) {
    case "src":
      return isAllowedEmailImageSrc(value, imagePrefixes);
    case "alt":
      return true;
    case "width":
    case "height":
      return isValidImageDimension(value);
    case "style":
      return normalizeImageStyle(value) !== null;
    default:
      return false;
  }
}

export type MarkupOptions = {
  /** Allowed image URL prefixes (default: crm.gavion.ai + the production email-assets bucket). */
  imagePrefixes?: readonly string[];
};

export type MarkupError = {
  code: "forbidden_tag" | "forbidden_attr" | "unbalanced_markup";
  at: number;
};

/** Null when every tag and attribute is allowed and tags nest properly. */
export function markupAllowlistError(
  html: string,
  opts: MarkupOptions = {},
): MarkupError | null {
  const { tokens, eof } = tokenize(html);
  const stack: string[] = [];
  for (const t of tokens) {
    if (t.type === "other") return { code: "forbidden_tag", at: t.at };
    if (!t.validName || !ALLOWED_TAGS.has(t.name))
      return { code: "forbidden_tag", at: t.at };
    if (t.type === "end") {
      if (t.attrs.length) return { code: "forbidden_attr", at: t.at };
      if (stack.pop() !== t.name)
        return { code: "unbalanced_markup", at: t.at };
      continue;
    }
    if (t.name === "img") {
      for (const a of t.attrs)
        if (!isAllowedImgAttr(a.name, a.value, opts.imagePrefixes))
          return { code: "forbidden_attr", at: a.at };
      if (!t.attrs.some((a) => a.name === "src"))
        return { code: "forbidden_attr", at: t.at };
      continue; // void
    }
    for (const a of t.attrs) {
      const allowed =
        ALLOWED_ATTRS.has(a.name) || (a.name === "href" && t.name === "a");
      // Markup-looking values are refused so no parser disagreement can turn them into tags.
      if (!allowed || /[<>]/.test(a.value))
        return { code: "forbidden_attr", at: a.at };
    }
    if (!VOID_TAGS.has(t.name)) stack.push(t.name);
  }
  if (eof) return { code: "unbalanced_markup", at: eof.at };
  return stack.length ? { code: "unbalanced_markup", at: html.length } : null;
}

// ---------------------------------------------------------------------------------------------
// URL attributes.
const URL_ATTRS = new Set([
  "href",
  "src",
  "srcset",
  "background",
  "poster",
  "action",
  "formaction",
  "data",
  "cite",
  "longdesc",
  "xlink:href",
]);
export const isUrlAttr = (name: string): boolean => URL_ATTRS.has(name);
const SAFE_SCHEME = /^(?:https:|mailto:)/i;

/** The URLs a browser would read from one attribute value (srcset: every candidate). */
export function urlsOf(name: string, value: string): string[] {
  if (name !== "srcset") return [htmlTrim(value)];
  return value.split(",").map((c) => htmlTrim(c).split(/[\t\n\f\r ]/)[0]);
}

/** Post-render check: the first URL attribute value that is not empty, https: or mailto:, else null. */
export function firstUnsafeUrlAttr(html: string): string | null {
  for (const t of tokenize(html).tokens) {
    if (t.type !== "start") continue;
    for (const a of t.attrs) {
      if (!isUrlAttr(a.name)) continue;
      for (const u of urlsOf(a.name, a.value))
        if (u && !SAFE_SCHEME.test(u)) return u;
    }
  }
  return null;
}
